import { Component, h, Ref, useEffect, useMemo } from "@lukekaalim/act";
import { a3 } from "@lukekaalim/act-three";
import { MiniGraphicState } from "./graphics";
import { GameAssetClient } from "@kosmod/assets";

import * as three from 'three';
import { Logic, TheatrePosition } from "../../../defs";
import { getAnimationPoint, useExternalRef, useLogger } from "@kosmod/kit";
import { renderBeat, useDisposable } from "@kosmod/graphics-3d";
import { calculateMiniAnimationPoint } from "../Mini";
import { TheatreSceneResources } from "../../../hooks";

export type QuickSpriteRendererProps = {
  graphicState: MiniGraphicState,

  quickSprite: Logic.StateByKey['render'],
  
  resources: TheatreSceneResources,
  textureLoader: three.TextureLoader,
  ref?: Ref<null | three.Sprite>,
}

const QUICKSPRITE_SELECTION_VERTICAL_MODIFIER = 0.1;
const QUICKSPRITE_SELECTION_SCALE_MODIFIER = 0.05;
const QUICKSPRITE_VOICE_JUMP_MODIFIER = .5;

export const QuickSpriteGraphicRenderer: Component<QuickSpriteRendererProps> = ({
  graphicState,
  quickSprite,
  resources,
  ref: externalRef = null,
}) => {
  if (quickSprite.data.renderType !== 'quick-sprite')
    return null;
  
  const logger = useLogger(`QuickSpriteGraphicRenderer`);

  const texture = quickSprite.data.spriteId && resources.miniSprites.get(quickSprite.data.spriteId)

  const material = useMemo(() => {
    // hot pink if no texture provided
    if (!texture)
      return new three.SpriteMaterial({ color: new three.Color('#ea07ee') });

    return new three.SpriteMaterial({
      map: texture,
      alphaHash: true,
      alphaTest: 0.2,
      color: new three.Color('#ffffff'),
    });
  }, [texture]);

  const ref = useExternalRef<three.Sprite | null>(externalRef, null);

  useEffect(() => {
    const sprite = ref.get();
    if (!texture || !sprite)
      return;

    texture.onUpdate = () => {
      sprite.scale
        .set(texture.width/texture.height, 1, 0)
    }
  }, [texture])

  renderBeat.useBeat((now) => {
    const sprite = ref.get();
    if (!sprite || !texture)
      return;

    // A final position is calculated, and then these "modifiers" are applied
    const positionModifier = new three.Vector3();
    const scaleModifier = new three.Vector3()

    let surfacePosition: three.Vector3;
    let theatrePosition: TheatrePosition = graphicState.position;

    // "movementAnimation" is when a character is moving from one grid position to another.
    // it might not be cleared when they arrive, so position calculations should be clamped.
    if (graphicState.movementAnimation && graphicState.movementAnimation.points.length > 0) {
      [surfacePosition, theatrePosition] = calculateMiniAnimationPoint(graphicState.position, graphicState.movementAnimation, Date.now());
    } else {
      surfacePosition = TheatrePosition.copyToVector3(graphicState.position);
    }
    // move into center
    surfacePosition.add({ x: .5, y: 0, z: .5 })

    // "Visibility" is a value that indicates whether the current player has "LINE OF SIGHT"
    let vis = 255;
    if (graphicState.visibilityChunks) {
      vis = graphicState.visibilityChunks.read(theatrePosition.vector.x, theatrePosition.vector.y);
    }
    if (graphicState.hasGodVision) {
      vis = Math.max(.1, vis);
    }

    // "Selection" covers both "hover" (a value of 0.5) and "selected" (a value of 1)
    if (graphicState.selectionAnimation) {
      const selectionPoint = getAnimationPoint(graphicState.selectionAnimation, now);
      positionModifier.add({ x: 0, y: selectionPoint * QUICKSPRITE_SELECTION_VERTICAL_MODIFIER, z: 0 });
      scaleModifier.addScalar(selectionPoint * QUICKSPRITE_SELECTION_SCALE_MODIFIER)
    }

    // Move the mini around its grid space to appear better for the camera
    // if the mini is viewed from high above (a big camera.vertical),
    // then it's "origin" should be more to the side closest to the camera,
    // so the sprite looks like it's filling the tile volume.c
    if (graphicState.cameraRef) {
      const camera = graphicState.cameraRef.get();
      if (camera) {
        const cameraRotationModification = new three.Vector3(
          camera.forward.x * camera.vertical * 0.1,
          0,
          camera.forward.y * camera.vertical * 0.1
        );

        cameraRotationModification.clampLength(0, 0.5)
        positionModifier.add(cameraRotationModification);
      }
    }

    if (graphicState.volumeRef) {
      const voiceVolume = graphicState.volumeRef.get();
      positionModifier.add({ x: 0, y: voiceVolume * QUICKSPRITE_VOICE_JUMP_MODIFIER, z: 0 })
    }

    const brightness = Math.min(1, vis * 50/255);
    material.color.set(brightness, brightness, brightness);

    sprite.visible = brightness > 0;

    sprite.position
      .copy(surfacePosition)
      .add(positionModifier);

    sprite.scale
      .set(texture.width/texture.height, 1, 0)
      .add(scaleModifier)
  })

  return h(a3.sprite, {
    material,
    ref,
    center: {
      x: .5,
      y: 0,
    }
  });
};